import { IChartBarData, handleIncomingBarData } from '@aqx-trader/chart-utils';
import { Time } from 'lightweight-charts';
import qs from 'qs';
import { RefObject } from 'react';
import { WebView } from 'react-native-webview';
import Service from 'react-reservice';

import { EMainProductCodeType } from '@webtrader/core/api/config/company';
import chartsAPI, { EChartPeriod } from '@webtrader/core/api/market/chart';
import { ISymbol } from '@webtrader/core/api/market/symbols';
import { EOrderType, IOrder } from '@webtrader/core/api/order';
import { IPendingOrder } from '@webtrader/core/api/order/pending';
import { IAssetStatisticResult, IProfitStatistic } from '@webtrader/core/api/statistics/account';
import { ITechnicalIndicator } from '@webtrader/core/api/user/indicator';
import { periodToDates, rawToBarData, trimBarData } from '@webtrader/core/chart/data';
import { EEventMessageType, IPostEventMessage, IReceiveEventMessage } from '@webtrader/core/chart/events';
import { IPostEventSetSymbolMessageData } from '@webtrader/core/chart/events/chart';
import { EChartIndicator, chartMainIndicators } from '@webtrader/core/chart/indicators';
import { EChartProvider } from '@webtrader/core/chart/provider';
import { EChartSeries } from '@webtrader/core/chart/series';
import configs from '@webtrader/core/configs';
import { EChartSource, EChartType, EIndicatorConfigType, ICustomBarData } from '@webtrader/core/constants/chart';
import dayjs from '@webtrader/core/dayjs';
import { numberOrDefault } from '@webtrader/core/format/number';
import { checkIsOpenPosition, getOrderBuySellType, getOrderTypeTextForChart } from '@webtrader/core/format/order';
import { trimToDecimal } from '@webtrader/core/format/price';
import i18n, { TLang, getLanguage } from '@webtrader/core/i18n';
import { domainUrl } from '@webtrader/core/request';
import { mixpanelAnalysisService } from '@webtrader/core/services/analysis';
import { chartService } from '@webtrader/core/services/chart/index';
import { companyService } from '@webtrader/core/services/company';
import { configService } from '@webtrader/core/services/config';
import { EAppType } from '@webtrader/core/services/config/service';
import { symbolsService } from '@webtrader/core/services/symbols';
import { themeService } from '@webtrader/core/services/theme/v2';
import { socketService } from '@webtrader/core/socket';
import { CHART_MESSAGE_TYPE, EChartTopicPeriod, IChartTopicReceiver } from '@webtrader/core/socket/topics/chart';
import storage, { EStorageKey } from '@webtrader/core/storage';
import { EAppTheme } from '@webtrader/core/themes';
import { IColors } from '@webtrader/core/themes/colors';
import { destructureAsNumber } from '@webtrader/core/utils';
import { checkIsClosableOrEditable } from '@webtrader/core/utils/order';
import { checkIsNative } from '@webtrader/core/validators/device';

import { EActionClick } from '../analysis/constants';
import { signalsService } from '../signals';
import { technicalIndicatorService } from '../technicalIndicator';
import { userPreferenceService } from '../userPreference';

export interface IContextMenuRef {
  open: (x: number, y: number) => void;
  close: () => void;
}

interface IGetEntryAndAdditionalPricesParams {
  orderType: EOrderType | undefined;
  price: number;
  priceTrigger: number;
}

interface IChartParams {
  period: EChartPeriod;
  series: EChartSeries;
  indicators: EChartIndicator[];
  metaTraderGroup: string;
  marketSubscription: string;
  colors?: IColors;
  source?: EChartSource;
  utcOffset?: number;
}

interface ICustomChartParams {
  type: EChartType;
  colors?: IColors;
  source?: EChartSource;
  assetsStatistic?: IAssetStatisticResult;
  profitsStatistic?: IProfitStatistic[];
}

interface IChartContext {
  key: TLang | null;
  fullscreen: boolean;
  portraitFullscreen: boolean;
  isChartReady: boolean;
  orderTrackers: IOrderTracker[];
  selectedPeriod: EChartPeriod;
  selectedSeries: EChartSeries;
  selectedProvider: EChartProvider;
  selectedIndicators: EChartIndicator[];
  askPriceVisible: boolean;
  colors?: IColors;
  activeSymbol?: string;

  // Indicator Configuration
  colorPickerVisible: boolean;
  selectedIndicatorTab: EIndicatorConfigType;
  isIndicatorLoading: boolean;

  isSymbolChartActive: boolean;
}

export interface IGetEntryPriceInfoParams {
  orderType: EOrderType;
  decimal: number;
  entryPrice: number;
  additionalPrice?: number;
}

export interface ITrackOrderParams {
  symbol: string;
  decimal: number;
  orderId: string;
  orderType: EOrderType;
  stopLoss: number;
  takeProfit: number;
  entryPrice: number;
  isEditable: boolean;
  isClosable: boolean;
  additionalPrice?: number;
}

export interface IOrderTracker {
  symbol: string;
  orderId: string;
}

interface IChartInitWebParams {
  iFrame: HTMLIFrameElement;
}

interface IChartInitNativeParams {
  webView: WebView;
}

interface IUpdateTrackOrderPriceParams {
  orderId: number;
  decimal: number;
  stopLoss?: number;
  takeProfit?: number;
  entryPrices?: number[];
}

interface IRetrackOrdersParams {
  orders: IOrder[];
  pendingOrders: IPendingOrder[];
  updateTrackers?: boolean;
}

const defaultContext: IChartContext = {
  key: null,
  fullscreen: false,
  portraitFullscreen: false,
  isChartReady: false,
  orderTrackers: [],
  selectedSeries: EChartSeries.CANDLESTICK,
  selectedPeriod: EChartPeriod.HOUR_4,
  selectedProvider: EChartProvider.ORIGINAL,
  selectedIndicators: [],
  askPriceVisible: false,

  // Indicator Configuration
  colorPickerVisible: false,
  selectedIndicatorTab: EIndicatorConfigType.INPUT,
  activeSymbol: undefined,
  isIndicatorLoading: false,
  isSymbolChartActive: false
};

class ChartService extends Service<IChartContext> {
  private cachedEventMessage: IPostEventMessage[] = [];

  private source?: EChartSource;
  private iFrame?: HTMLIFrameElement;
  private webView?: WebView;

  private barData: ICustomBarData[] = []; // Declare as private to prevent rerender in context
  private previousAccessedBarData: ICustomBarData[] = [];
  private previousAccessedSymbol?: string;
  private decimals = 2;
  private useNewDataAsOpen = false;
  private fetchingMore = false;

  private chartAbortController = new AbortController();
  private chartLatestPriceAbortController = new AbortController();
  private chartLatestBarAbortController = new AbortController();
  private fetchMoreAbortController = new AbortController();

  constructor() {
    super();

    this.context = {
      ...defaultContext,
      selectedIndicators: this.storageIndicators(),
      askPriceVisible: this.storageAskPriceVisible()
    };
  }

  public reset = () => {
    this.context.fullscreen = false;
    this.context.portraitFullscreen = this.context.selectedProvider === EChartProvider.TRADING_VIEW;
    this.context.isChartReady = false;

    this.previousAccessedSymbol = this.context.activeSymbol;
    this.context.activeSymbol = undefined;
    this.fetchingMore = false;

    if (this.barData.length > 0) {
      this.previousAccessedBarData = this.barData;
      this.barData = [];
    }
    this.context.isSymbolChartActive = false;
  };

  public setSymbolChartActive = () => {
    this.context.isSymbolChartActive = true;
  };

  public setTvInitialConfigs = () => {
    const { positive, negative } = userPreferenceService.context.candlestickColorUserPreference || {};
    const { background } = themeService.context.theme.colors || {};

    const data = {
      period: chartService.context.selectedPeriod,
      isServerTime: companyService.getMarketTimeIsServer(),
      serverUtcOffset: companyService.getTimezoneOffset(),
      localTimezone: dayjs.tz.guess(),
      languageCode: getLanguage(),
      theme: themeService.isDarkTheme() ? EAppTheme.DARK : EAppTheme.LIGHT,
      series: chartService.context.selectedSeries,
      candlestickColor: {
        positive,
        negative
      },
      backgroundColor: background[0]
    };
    this.postEventMessage({
      type: EEventMessageType.TV_SET_INITIAL_CONFIGS,
      data
    });
  };

  public setProvider = (provider: EChartProvider) => {
    this.context.selectedProvider = provider;

    this.resetChartReadyStatus();

    const { selectedSymbol: selectedHomeSymbol } = symbolsService.context;
    const { selectedSymbolDetails: selectedSignalSymbol } = signalsService.context || {};

    const selectedSymbol = selectedHomeSymbol || selectedSignalSymbol;

    if (selectedSymbol) {
      const isMobile = configService.getAppType() === EAppType.MOBILE;
      if (provider === EChartProvider.ORIGINAL) {
        chartService.getCandlestickColorUserPreference();
        this.emitEnabledIndicatorsToChart();
        if (isMobile) {
          this.context.portraitFullscreen = this.context.fullscreen;
        }
      } else {
        this.setTvInitialConfigs();
        if (isMobile) {
          this.context.portraitFullscreen = true;
        }
      }

      const { decimal, symbol, description } = selectedSymbol;

      this.onSelectSymbol(symbol, decimal, {
        description
      });
    }
  };

  public resetChartReadyStatus = () => {
    this.context.isChartReady = false;
  };

  public setLanguage = (lang: TLang) => {
    if (chartService.context.selectedProvider === EChartProvider.TRADING_VIEW) {
      this.postEventMessage({
        type: EEventMessageType.TV_SET_LANGUAGE,
        data: lang
      });

      return;
    }

    this.context.isChartReady = false;

    const { selectedSymbol } = symbolsService.context;

    if (selectedSymbol) {
      const { decimal, symbol } = selectedSymbol;

      this.onSelectSymbol(symbol, decimal);
    }

    this.context.key = lang;

    this.emitEnabledIndicatorsToChart();
  };

  public resetLanguage = () => {
    this.context.key = null;
  };

  public setSource = (source: EChartSource) => {
    this.source = source;
  };

  public initWeb = (params: IChartInitWebParams) => {
    const { iFrame } = params;

    this.iFrame = iFrame;
  };

  public initNative = (params: IChartInitNativeParams) => {
    const { webView } = params;

    this.context.selectedIndicators = this.storageIndicators();

    this.webView = webView;
    if (this.context.selectedProvider === EChartProvider.TRADING_VIEW) {
      this.context.portraitFullscreen = true;
    }
  };

  public cleanOrderTrackers = (symbol: string) => {
    const trackersToRemove = this.context.orderTrackers.filter((tracker) => tracker.symbol !== symbol);

    if (this.context.orderTrackers.length > 0) {
      this.postEventMessage({
        type: EEventMessageType.BATCH_CANCEL_TRACKER_ORDER,
        data: trackersToRemove.map((tracker) => Number(tracker.orderId))
      });
    }

    this.context.orderTrackers = this.context.orderTrackers.filter((tracker) => tracker.symbol === symbol);
  };

  public cleanAllOrderTrackers = () => {
    const trackersToRemove = this.context.orderTrackers;

    if (trackersToRemove.length > 0) {
      this.postEventMessage({
        type: EEventMessageType.BATCH_CANCEL_TRACKER_ORDER,
        data: trackersToRemove.map((tracker) => Number(tracker.orderId))
      });
      this.context.orderTrackers = [];
    }
  };

  private getEntryPriceInfo = ({ orderType, decimal, entryPrice, additionalPrice }: IGetEntryPriceInfoParams) => {
    const appType = configService.getAppType();
    const orderTypeText = getOrderTypeTextForChart(orderType);

    if (orderType === EOrderType.BUY_STOP_LIMIT || orderType === EOrderType.SELL_STOP_LIMIT) {
      return [
        {
          formatStr:
            appType === EAppType.PC
              ? `${orderTypeText} {orderId} | ${i18n.t('Stop Limit Price')} @ {price}`
              : `${orderTypeText} {orderId} | ${i18n.t('Stop Limit Price')}`,
          price: trimToDecimal(entryPrice, decimal) ?? 0
        },
        {
          formatStr: appType === EAppType.PC ? `${orderTypeText} {orderId} @ {price}` : `${orderTypeText} {orderId}`,
          price: trimToDecimal(additionalPrice, decimal) ?? 0
        }
      ];
    } else {
      return [
        {
          formatStr: appType === EAppType.PC ? `${orderTypeText} {orderId} @ {price}` : `${orderTypeText} {orderId}`,
          price: trimToDecimal(entryPrice, decimal) ?? 0
        }
      ];
    }
  };

  public getEntryAndAdditionalPrices = ({
    orderType,
    price,
    priceTrigger
  }: IGetEntryAndAdditionalPricesParams): {
    entryPrice: number;
    additionalPrice?: number;
  } => {
    switch (orderType) {
      case EOrderType.BUY_STOP_LIMIT:
      case EOrderType.SELL_STOP_LIMIT:
        return {
          entryPrice: priceTrigger,
          additionalPrice: price
        };

      default:
        return {
          entryPrice: price,
          additionalPrice: undefined
        };
    }
  };

  public trackOrder = (params: ITrackOrderParams, addToTrackers = true) => {
    const {
      symbol,
      decimal,
      orderId,
      orderType,
      stopLoss,
      takeProfit,
      entryPrice,
      additionalPrice,
      isClosable,
      isEditable
    } = params;

    const orderTrackers = this.context.orderTrackers;
    const isDiffSymbol = orderTrackers.length > 0 && orderTrackers.some((tracker) => tracker.symbol !== symbol);

    if (isDiffSymbol) {
      this.cleanOrderTrackers(symbol);
    }

    if (addToTrackers) {
      this.context.orderTrackers = [
        ...this.context.orderTrackers,
        {
          symbol,
          orderId
        }
      ];
    }

    this.postEventMessage({
      type: EEventMessageType.TRACK_ORDER,
      data: {
        symbol,
        decimal,
        orderId: Number(orderId),
        stopLoss: trimToDecimal(stopLoss, decimal) ?? 0,
        takeProfit: trimToDecimal(takeProfit, decimal) ?? 0,
        entryPriceInfo: this.getEntryPriceInfo({ orderType, decimal, entryPrice, additionalPrice }),
        isPendingOrder: !checkIsOpenPosition(orderType),
        buySellType: getOrderBuySellType(orderType),
        isEditable,
        isClosable
      }
    });
  };

  public cancelTrackOrder = (orderId: string) => {
    this.context.orderTrackers = this.context.orderTrackers.filter((tracker) => tracker.orderId !== orderId);

    this.postEventMessage({
      type: EEventMessageType.CANCEL_TRACKER_ORDER,
      data: Number(orderId)
    });
  };

  public updateTrackOrder = (newOrders: string[]) => {
    const trackersToRemove = this.context.orderTrackers.filter(
      (tracker) => !newOrders.some((order) => order === tracker.orderId)
    );

    if (trackersToRemove.length > 0) {
      trackersToRemove.map((tracker) => {
        this.context.orderTrackers = [
          ...this.context.orderTrackers.filter((orderTracker) => orderTracker.orderId !== tracker.orderId)
        ];
      });

      this.postEventMessage({
        type: EEventMessageType.BATCH_CANCEL_TRACKER_ORDER,
        data: trackersToRemove.map((tracker) => Number(tracker.orderId))
      });
    }
  };

  private identifyAndRetrackOrder = (order: IOrder | IPendingOrder) => {
    switch (order.orderType) {
      case EOrderType.BUY:
      case EOrderType.SELL:
        {
          const {
            symbol,
            decimal,
            orderId,
            orderType,
            stopLoss,
            takeProfit,
            openPrice,
            closable,
            editable,
            marketFillPolicy
          } = order as IOrder;
          this.trackOrder(
            {
              symbol,
              decimal,
              orderId,
              orderType,
              stopLoss: numberOrDefault(stopLoss),
              takeProfit: numberOrDefault(takeProfit),
              entryPrice: numberOrDefault(openPrice),
              isClosable: checkIsClosableOrEditable({ marketFillPolicy, flag: closable }),
              isEditable: checkIsClosableOrEditable({ marketFillPolicy, flag: editable })
            },
            false
          );
        }
        break;
      case EOrderType.BUY_LIMIT:
      case EOrderType.BUY_STOP:
      case EOrderType.BUY_STOP_LIMIT:
      case EOrderType.SELL_LIMIT:
      case EOrderType.SELL_STOP:
      case EOrderType.SELL_STOP_LIMIT:
        {
          const {
            symbol,
            decimal,
            orderId,
            orderType,
            stopLoss,
            takeProfit,
            price,
            priceTrigger,
            closable,
            editable,
            marketFillPolicy
          } = order as IPendingOrder;

          const { entryPrice, additionalPrice } = chartService.getEntryAndAdditionalPrices({
            orderType,
            price,
            priceTrigger
          });

          this.trackOrder(
            {
              symbol,
              decimal,
              orderId,
              orderType,
              stopLoss: numberOrDefault(stopLoss),
              takeProfit: numberOrDefault(takeProfit),
              entryPrice: entryPrice,
              additionalPrice: additionalPrice,
              isClosable: checkIsClosableOrEditable({ marketFillPolicy, flag: closable }),
              isEditable: checkIsClosableOrEditable({ marketFillPolicy, flag: editable })
            },
            false
          );
        }
        break;
    }
  };

  public retrackOrders = ({ orders, pendingOrders, updateTrackers }: IRetrackOrdersParams) => {
    const { orderTrackers } = this.context;
    if (orderTrackers.length === 0) {
      return;
    }

    const allTrackedOrderIds = orderTrackers.map(({ orderId }) => Number(orderId));

    // Remove all trackers first
    this.postEventMessage({
      type: EEventMessageType.BATCH_CANCEL_TRACKER_ORDER,
      data: allTrackedOrderIds
    });

    const trackedOrders: Record<number, IOrder | IPendingOrder> = {};
    for (const order of orders) {
      const { orderId } = order;
      const orderIdNum = Number(orderId);
      if (allTrackedOrderIds.includes(orderIdNum)) {
        trackedOrders[orderIdNum] = order;
      }
    }
    for (const pendingOrder of pendingOrders) {
      const { orderId } = pendingOrder;
      const orderIdNum = Number(orderId);
      if (allTrackedOrderIds.includes(orderIdNum)) {
        trackedOrders[orderIdNum] = pendingOrder;
      }
    }

    const newOrderTrackers = orderTrackers.filter(({ orderId }) => orderId in trackedOrders);

    if (updateTrackers) {
      this.context.orderTrackers = newOrderTrackers;
    }

    for (const { orderId } of newOrderTrackers) {
      const trackedOrder = trackedOrders[Number(orderId)];
      if (trackedOrder) {
        this.identifyAndRetrackOrder(trackedOrder);
      }
    }
  };

  public updateTrackOrderPrice = (params: IUpdateTrackOrderPriceParams) => {
    const { orderId, decimal, stopLoss, takeProfit, entryPrices } = params;

    this.postEventMessage({
      type: EEventMessageType.UPDATE_TRACK_ORDER_PRICES,
      data: {
        orderId,
        stopLoss: trimToDecimal(stopLoss, decimal) ?? 0,
        takeProfit: trimToDecimal(takeProfit, decimal) ?? 0,
        entryPrices: entryPrices?.map((price) => trimToDecimal(price, decimal) ?? 0)
      }
    });
  };

  public clearOrderTrackers = () => {
    this.context.orderTrackers = [];
  };

  public webViewUrl = (customColors?: IColors, provider?: EChartProvider) => {
    const queryStrings = this.queryStringsForWebView(customColors);

    if (provider === EChartProvider.TRADING_VIEW) {
      const TV_PATH = '/tv';
      if (checkIsNative()) {
        // On Android, localhost refers to the Android device itself, not your development machine.
        // return 'http://10.0.2.2:3007'; // using local TV testing for Android
        // return 'http://localhost:3007'; // using local TV testing for IOS
        const remotePath = `${
          configService.getCurrentApiDomain() === configs.apiDomainFallback
            ? configService.getCurrentStaticDomain()
            : domainUrl
        }${TV_PATH}`;
        return remotePath; // using current deployed TV
      }
      return NODE_ENV === 'development' ? 'http://localhost:3007' : TV_PATH;
    }
    const baseUrl = checkIsNative() ? domainUrl : '';
    return `${baseUrl}/chart?${queryStrings}`;
  };

  public assetsWebViewUrl = (colors?: IColors, assetsStatistic?: IAssetStatisticResult) => {
    const queryStrings = this.queryStringsForCustomChart({
      type: EChartType.ASSETS,
      colors: colors,
      assetsStatistic: assetsStatistic
    });
    const baseUrl = checkIsNative() ? domainUrl : '';
    return `${baseUrl}/chart?${queryStrings}`;
  };

  public profitLossWebViewUrl = (colors?: IColors, profitsStatistic?: IProfitStatistic[]) => {
    const queryStrings = this.queryStringsForCustomChart({
      type: EChartType.PROFIT_LOSS,
      colors: colors,
      profitsStatistic: profitsStatistic
    });
    const baseUrl = checkIsNative() ? domainUrl : '';
    return `${baseUrl}/chart?${queryStrings}`;
  };

  public onSelectSymbol = async (symbol: string, decimals: number, metadata?: Partial<ISymbol>) => {
    const postEventMessageData: IPostEventSetSymbolMessageData = { symbol };

    if (this.context.selectedProvider === EChartProvider.ORIGINAL) {
      this.postEventMessage({
        type: EEventMessageType.CHART_LOAD,
        data: true
      });
      this.postEventMessage({
        type: EEventMessageType.SET_SYMBOL,
        data: postEventMessageData
      });

      // Always fetch chart data
      // For mobile app, there will be postExistingChartData() to post previous chart data to webview while waiting for the new chart data
      // See: postExistingChartData()
      await this.fetchChartData(symbol, decimals);
    } else {
      const isPcWeb = configService.getAppType() === EAppType.PC;
      if (isPcWeb && !this.context.isSymbolChartActive) {
        return;
      }
      // Don't need call this.fetchChartData because Chart API will be called in TV instead
      // Only need to handle the symbol subscription here
      this.setTvInitialConfigs();
      this.postEventMessage({
        type: EEventMessageType.SET_SYMBOL,
        data: {
          ...postEventMessageData,
          metadata: {
            ...metadata,
            decimal: decimals
          }
        }
      });

      const { selectedPeriod: period, activeSymbol } = this.context;
      this.previousAccessedSymbol = this.context.activeSymbol;
      if (symbol !== activeSymbol) {
        this.context.activeSymbol = symbol;
      }

      this.handleChartTopicSubscriptions(symbol, period);
    }
  };

  public onSelectSeries = async (series: EChartSeries) => {
    this.postEventMessage({
      type: EEventMessageType.CHART_LOAD,
      data: true
    });

    this.context.selectedSeries = series;

    if (series === EChartSeries.LINE_ASK) {
      // Remove all selected main indicators when selected series is line with ask price
      const technicalIndicatorList: ITechnicalIndicator[] = [];
      for (const indicator of chartMainIndicators) {
        technicalIndicatorList.push({
          type: indicator,
          enabled: false
        });
      }

      await technicalIndicatorService.clearAllTechnicalIndicators({ technicalIndicatorList });

      const technicalIndicators = technicalIndicatorService.context.technicalIndicators;

      for (const indicator of technicalIndicators) {
        const included = chartMainIndicators.find((i) => i === indicator.type);
        if (indicator.enabled && included) {
          this.postEventMessage({
            type: EEventMessageType.INDICATOR,
            data: indicator.type
          });
        }
      }
    }

    this.postEventMessage({
      type: EEventMessageType.SERIES,
      data: series
    });
  };

  public onSelectPeriod = async (period: EChartPeriod) => {
    const previousPeriod = this.context.selectedPeriod;
    this.context.selectedPeriod = period;
    const symbol = this.context.activeSymbol;

    if (chartService.context.selectedProvider === EChartProvider.TRADING_VIEW) {
      this.postEventMessage({
        type: EEventMessageType.TV_SET_PERIOD,
        data: period
      });

      if (symbol) {
        this.unsubscribeChartTopic(symbol, previousPeriod);
        this.subscribeChartTopic(symbol, period);
      }
    } else {
      this.postEventMessage({
        type: EEventMessageType.CHART_LOAD,
        data: true
      });

      if (symbol) {
        await this.fetchChartData(symbol);

        this.unsubscribeChartTopic(symbol, previousPeriod);
        this.subscribeChartTopic(symbol, period);
      }

      const eventMessage = {
        chartData: this.barData,
        period
      };

      this.postEventMessage({
        type: EEventMessageType.PERIOD,
        data: eventMessage
      });
    }
  };

  public setTVTheme = (theme: EAppTheme) => {
    const { background } = themeService.context.theme.colors || {};

    this.postEventMessage({
      type: EEventMessageType.TV_CHANGE_THEME,
      data: {
        theme,
        backgroundColor: background[0]
      }
    });
  };

  public toggleTVIndicators = () => {
    this.postEventMessage({
      type: EEventMessageType.TV_TOGGLE_INDICATORS
    });
  };

  public onSelectIndicator = async (indicator: EChartIndicator, symbol?: string, onSuccess?: () => void) => {
    this.context.isIndicatorLoading = true;

    await technicalIndicatorService.setTechnicalIndicators(
      indicator,
      () => {
        this.context.isIndicatorLoading = false;
      },
      (isIndicatorEnabled) => {
        if (isIndicatorEnabled) {
          mixpanelAnalysisService.chart.indicatorSelect({
            symbol: symbol ?? '-',
            indicator: indicator?.toUpperCase()
          });
        }

        this.postEventMessage({
          type: EEventMessageType.INDICATOR,
          data: indicator
        });

        onSuccess?.();
      }
    );
  };

  public onToggleAskPriceVisible = () => {
    const newVisible = !this.context.askPriceVisible;

    storage.setItem(EStorageKey.ASK_PRICE_VISIBLE, newVisible);

    this.context.askPriceVisible = newVisible;

    this.postEventMessage({
      type: EEventMessageType.ASK_PRICE_VISIBLE,
      data: newVisible
    });
  };

  private handleMixpanelTrack = (data: IReceiveEventMessage) => {
    const { selectedSymbol } = symbolsService.context;
    const symbol = selectedSymbol?.symbol ?? '-';
    switch (data.type) {
      case EEventMessageType.OPEN_CONTEXT_MENU:
        mixpanelAnalysisService.page.actionClick({
          action: EActionClick.CHART_RIGHT_CLICKED,
          symbol
        });
        break;
      case EEventMessageType.SELECT_DRAWING_TOOL:
        mixpanelAnalysisService.chart.chartDrawToolSelected({
          symbol,
          drawingTool: data.data
        });
        break;
      case EEventMessageType.COMPLETE_DRAWING:
        mixpanelAnalysisService.chart.chartDrawingCompleted({
          symbol,
          drawingTool: data.data
        });
        break;
      case EEventMessageType.DELETE_DRAWING:
        mixpanelAnalysisService.chart.chartDeleteDrawingClicked({
          symbol,
          drawingTool: data.data
        });
        break;
    }
  };

  public onReceiveEventMessage = (data: IReceiveEventMessage, contextMenuRef?: RefObject<IContextMenuRef>) => {
    // Function to track mixpanl event
    this.handleMixpanelTrack(data);

    switch (data.type) {
      case EEventMessageType.OPEN_CONTEXT_MENU:
        contextMenuRef?.current?.open(data.data.x, data.data.y);
        break;
      case EEventMessageType.CHART_FETCH_MORE:
        const { latestFrom, latestTo } = data.data;
        if (latestFrom && latestTo) {
          this.fetchMoreChartData(latestFrom, latestTo);
        }
        break;
      case EEventMessageType.INDICATOR:
        this.onSelectIndicator(data.data);
        break;
      case EEventMessageType.INDICATOR_LOAD:
        this.context.isIndicatorLoading = data.data;
        break;
      case EEventMessageType.STATUS:
        const chartOkay = data.data;
        if (chartOkay) {
          this.postEventMessage({
            type: EEventMessageType.ASK_PRICE_VISIBLE,
            data: this.context.askPriceVisible
          });
        }
        if (!this.context.isChartReady) {
          this.context.isChartReady = chartOkay;
          this.dispatchCachedMessages();
        }
        break;
    }
  };

  public postEventMessage = (message: IPostEventMessage, directPost?: boolean) => {
    const { isChartReady } = this.context;
    const { source } = this;

    if (isChartReady || directPost) {
      switch (source) {
        case EChartSource.MOBILEAPP:
          this.postNativeEventMessage(message);
          break;
        default:
          this.postWebEventMessage(message);
          break;
      }
    } else {
      this.cachedEventMessage.push(message);
    }
  };

  public scrollToLatest = () => {
    this.postEventMessage({
      type: EEventMessageType.SCROLL_TO_LATEST
    });
  };

  public switchFullscreen = () => {
    this.context.fullscreen = !this.context.fullscreen;
  };

  public switchPortraitFullscreen = () => {
    this.context.portraitFullscreen = !this.context.portraitFullscreen;
    if (this.context.fullscreen) {
      this.context.fullscreen = false;
    }
  };

  public dispatchCachedMessages = () => {
    const { isChartReady } = this.context;
    const { cachedEventMessage } = this;

    if (isChartReady && cachedEventMessage.length > 0) {
      this.cachedEventMessage = [];

      cachedEventMessage.forEach((message) => {
        this.postEventMessage(message);
      });
    }
  };

  public detachIFrame = () => {
    this.context.isChartReady = false;
    this.iFrame = undefined;
  };

  public detachWebview = () => {
    this.context.isChartReady = false;
    this.webView = undefined;
  };

  public resetChart = () => {
    this.cleanAllOrderTrackers();
    this.onSelectPeriod(EChartPeriod.HOUR_4);
    this.resetIndicator();
    this.resetSeries();
    this.resetAskPrice();

    this.postEventMessage({
      type: EEventMessageType.RESET
    });
  };

  public resetDrawing = () => {
    this.postEventMessage({
      type: EEventMessageType.REMOVE_DRAWING
    });
  };

  public resetIndicator = async () => {
    this.context.selectedIndicators = [];
    storage.setItem(EStorageKey.INDICATORS, []);

    const technicalIndicators = technicalIndicatorService.context.technicalIndicators;

    const technicalIndicatorList: ITechnicalIndicator[] = [];
    for (const indicator of technicalIndicators) {
      technicalIndicatorList.push({
        type: indicator.type,
        enabled: false
      });
    }

    await technicalIndicatorService.clearAllTechnicalIndicators({ technicalIndicatorList });

    for (const indicator of technicalIndicators) {
      if (indicator.enabled) {
        this.postEventMessage({
          type: EEventMessageType.INDICATOR,
          data: indicator.type
        });
      }
    }
  };

  public resetSeries = () => {
    const selectedSeries = this.context.selectedSeries;
    const isMT5 = companyService.getIntegration() === EMainProductCodeType.METATRADER5;
    if (isMT5 && selectedSeries === EChartSeries.LINE_ASK) {
      this.context.selectedSeries = EChartSeries.LINE;
      this.postEventMessage({
        type: EEventMessageType.SERIES,
        data: EChartSeries.LINE
      });
    }
  };

  public resetAskPrice = () => {
    storage.setItem(EStorageKey.ASK_PRICE_VISIBLE, false);

    this.context.askPriceVisible = false;

    this.postEventMessage({
      type: EEventMessageType.ASK_PRICE_VISIBLE,
      data: false
    });
  };

  public initFirstLoadTechnicalIndicator = (technicalIndicators: ITechnicalIndicator[]) => {
    if (technicalIndicators.length <= 0) {
      return;
    }

    for (const indicator of technicalIndicators) {
      if (!!indicator.enabled) {
        this.postEventMessage({
          type: EEventMessageType.INDICATOR,
          data: indicator.type,
          needSort: true
        });
      }
    }
  };

  public onChartLoad = () => {
    this.initChartToken();
    this.setPostMessageSource();
  };

  private setPostMessageSource = () => {
    const isNative = checkIsNative();
    const isTradingView = this.context.selectedProvider === EChartProvider.TRADING_VIEW;
    if (isNative && isTradingView) {
      this.postEventMessage(
        {
          type: EEventMessageType.IS_NATIVE
        },
        true
      );
    }
  };

  public initChartToken = () => {
    const token = storage.getItem<string>(EStorageKey.TOKEN);

    this.postEventMessage(
      {
        type: EEventMessageType.TOKEN,
        data: token
      },
      true
    );
  };

  // Chart Optimisation Section
  // Main function to fetch bar data to post to chart
  public fetchChartData = async (symbol: string, decimal?: number) => {
    const { selectedPeriod: period, activeSymbol } = this.context;
    const symbolDecimals = decimal ?? this.decimals;

    if (this.chartAbortController) {
      this.chartAbortController.abort();
    }
    if (this.chartLatestBarAbortController) {
      this.chartLatestBarAbortController.abort();
    }
    if (this.chartLatestPriceAbortController) {
      this.chartLatestPriceAbortController.abort();
    }
    this.chartAbortController = new AbortController();
    this.chartLatestBarAbortController = new AbortController();
    this.chartLatestPriceAbortController = new AbortController();

    const periodRange = periodToDates({
      period
    });

    // Comment out this for now as the latest chart time is not in used in most of the time
    // const { ask: latestAsk } = latest || {};
    // const minFrom = Math.min(periodRange.from, latestChartTime ?? 0);

    const [chartLatestPrice, chartAPIData, latestChartBar] = await Promise.all([
      this.fetchLatestPriceAPI(symbol, period, this.chartLatestPriceAbortController?.signal), // Latest Price
      this.fetchHistoricalChartAPI(symbol, period, periodRange.from, periodRange.to, this.chartAbortController?.signal), // Historical
      this.fetchLatestBarAPI(symbol, period, periodRange.to, this.chartLatestBarAbortController?.signal) // Latest Bar
    ]);

    const { ask: latestAsk } = chartLatestPrice || {};

    if (chartAPIData && latestChartBar) {
      this.previousAccessedSymbol = this.context.activeSymbol;

      let fullBarAPIData = chartAPIData;
      // TODO - There might be a chance where the historical candlestick return the latest bar , in that case do not replace the historical data with the latestChartBar
      if (chartAPIData[chartAPIData.length - 1].chartTime !== latestChartBar.chartTime) {
        fullBarAPIData = [...chartAPIData, latestChartBar];
      }

      const barData = rawToBarData(fullBarAPIData, decimal);

      if (symbol !== activeSymbol) {
        this.context.activeSymbol = symbol;
      }
      // Add latest ask price from chartsLatestAPI to last data if the chart series is not Line (Bid & Ask)
      if (barData.length && this.context.selectedSeries !== EChartSeries.LINE_ASK) {
        barData[barData.length - 1].ask = latestAsk;
      }

      this.barData = barData;
      this.decimals = symbolDecimals;

      this.postEventMessage({
        type: EEventMessageType.SYMBOL_CHART_DATA,
        data: {
          barData,
          decimals: symbolDecimals
        }
      });
    }

    this.handleChartTopicSubscriptions(symbol, period);
  };

  // Only for mobile app
  // For users accessing Trade page of same previous symbol
  // Post the existing chart data while fetching the latest data in onSelectSymbol()
  // See: onSelectSymbol()
  public postExistingChartData = () => {
    const { selectedPeriod, activeSymbol } = this.context;
    const { decimals, previousAccessedBarData, previousAccessedSymbol } = this;

    if (activeSymbol && activeSymbol === previousAccessedSymbol) {
      this.barData = previousAccessedBarData;
      this.postEventMessage({
        type: EEventMessageType.SYMBOL_CHART_DATA,
        data: {
          barData: previousAccessedBarData,
          decimals
        }
      });

      this.handleChartTopicSubscriptions(activeSymbol, selectedPeriod);
    }
  };

  private fetchLatestPriceAPI = async (symbol: string, period: EChartPeriod, abortSignal?: AbortSignal) => {
    const { result } = await chartsAPI.latestPrice(
      {
        symbol,
        period
      },
      abortSignal
    );
    return result;
  };

  private fetchHistoricalChartAPI = async (
    symbol: string,
    period: EChartPeriod,
    from: number,
    to: number,
    abortSignal?: AbortSignal
  ) => {
    const { result } = await chartsAPI.historicalChart(
      {
        to,
        from,
        symbol,
        period
      },
      abortSignal
    );
    return result;
  };

  private fetchLatestBarAPI = async (symbol: string, period: EChartPeriod, to: number, abortSignal?: AbortSignal) => {
    const { result } = await chartsAPI.latestBarData(
      {
        to,
        symbol,
        period
      },
      abortSignal
    );
    return result;
  };

  // Fetch more data when the first candlestick is in the logical range of the chart
  private fetchMoreChartData = async (latestFrom: number, latestTo: number) => {
    try {
      const { selectedPeriod: period, activeSymbol: symbol } = this.context;
      const { barData, fetchingMore } = this;

      if (symbol && !fetchingMore) {
        this.fetchingMore = true;
        if (this.fetchMoreAbortController) {
          this.fetchMoreAbortController.abort();
        }
        this.fetchMoreAbortController = new AbortController();
        const { to, from } = periodToDates({
          period,
          latestFrom,
          latestTo,
          dateToUsesEndOfDay: false
        });
        const result = await this.fetchHistoricalChartAPI(
          symbol,
          period,
          from,
          to,
          this.fetchMoreAbortController?.signal
        );
        if (result && result.length > 0) {
          const newBarData = trimBarData(rawToBarData(result, this.decimals), barData[0].time as number);
          this.barData = [...newBarData, ...barData];
          this.postEventMessage({
            type: EEventMessageType.CHART_FEED,
            data: this.barData
          });
        }
      }
    } catch (err) {
    } finally {
      setTimeout(() => {
        this.fetchingMore = false;
      }, 500);
    }
  };

  public refetchChartData = async () => {
    const { activeSymbol: symbol, selectedPeriod: period } = this.context;
    if (symbol && period) {
      await this.fetchChartData(symbol, this.decimals);
    }
  };

  private postWebEventMessage = (message: IPostEventMessage) => {
    const { iFrame } = this;

    if (iFrame) {
      iFrame.contentWindow?.postMessage(message, '*');
    }
  };

  private postNativeEventMessage = (message: IPostEventMessage) => {
    const { webView } = this;

    // check out this site
    // https://github.com/react-native-webview/react-native-webview/releases/tag/v5.0.0
    const postScript = `
      window.postMessage(${JSON.stringify(message)});

      true;
    `;

    if (webView) {
      webView.injectJavaScript(postScript);
    }
  };

  private queryStringsForCustomChart = (param: ICustomChartParams) => {
    const params: ICustomChartParams = {
      type: param.type,
      colors: param.colors || themeService.context.theme.colors,
      source: this.source,
      assetsStatistic: param.assetsStatistic,
      profitsStatistic: param.profitsStatistic
    };

    return qs.stringify(params);
  };

  private queryStringsForWebView = (customColors?: IColors) => {
    const { selectedPeriod, selectedSeries } = this.context;

    const utcOffset = companyService.getTimezoneOffset();

    const metaTraderGroup = storage.getItem<string>(EStorageKey.META_TRADER_GROUP);
    const marketSubscription = storage.getItem<string>(EStorageKey.MARKET_SUBSCRIPTION);

    const params: IChartParams = {
      colors: customColors || themeService.context.theme.colors,
      period: selectedPeriod,
      series: selectedSeries,
      source: this.source,
      utcOffset,
      indicators: [],
      metaTraderGroup,
      marketSubscription
    };

    return qs.stringify(params);
  };

  private storageIndicators = () => {
    return storage.getItem<EChartIndicator[]>(EStorageKey.INDICATORS) || [];
  };

  private storageAskPriceVisible = () => storage.getItem<boolean>(EStorageKey.ASK_PRICE_VISIBLE) || false;

  // Chart Section
  private handleChartTopicSubscriptions = async (symbol: string, period: EChartPeriod) => {
    const previousAccessedSymbol = this.previousAccessedSymbol;

    if (previousAccessedSymbol) {
      this.unsubscribeChartTopic(previousAccessedSymbol, period);
    }

    // symbol topic subscription is handled by useSymbolTopic
    this.subscribeChartTopic(symbol, period);
    this.previousAccessedSymbol = symbol;
  };

  private subscribeChartTopic = async (symbol: string, period: EChartPeriod) => {
    socketService.topics?.()?.chart.subscribe({
      period: this.mapChartPeriodToTopicPeriod(period),
      symbol,
      receiver: this.onReceiveChartMessage
    });
  };

  private unsubscribeChartTopic = (symbol: string, period: EChartPeriod) => {
    socketService.topics?.()?.chart.unsubscribe({
      period: this.mapChartPeriodToTopicPeriod(period),
      symbol,
      receiver: this.onReceiveChartMessage
    });
  };

  private onReceiveChartMessage: IChartTopicReceiver = (message) => {
    const { isChartReady, activeSymbol: symbol } = this.context;

    if (!message || !isChartReady || !symbol) {
      return;
    }

    switch (message.M) {
      case CHART_MESSAGE_TYPE.MOVE:
        /** Remove for now as the socket might be delay in result to the incorrect formation of candlestick */
        // this.generateNewBarData();
        break;
      case CHART_MESSAGE_TYPE.PATCH:
        this.patchChartData({
          low: message.D.L,
          high: message.D.H,
          open: message.D.O,
          close: message.D.C,
          ask: message.D.A,
          time: (message.D.T / 1000) as Time
        });
        break;
      default:
        break;
    }
  };

  private patchChartData = (dataItem: ICustomBarData) => {
    const { barData } = this;

    const newBarData = [...barData];

    for (let i = newBarData.length - 1; i > -1; i--) {
      const currItem = newBarData[i];

      if (currItem.time === dataItem.time) {
        const { ask, open, close, high, low } = destructureAsNumber(currItem, ['ask', 'open', 'close', 'high', 'low']);
        const { ask: newAsk, open: newOpen, close: newClose, high: newHigh, low: newLow } = dataItem;
        const replaceBarData = {
          ...dataItem,
          ask: newAsk ?? ask,
          open: newOpen ?? open,
          close: newClose ?? close,
          high: newHigh ?? high,
          low: newLow ?? low
        };
        newBarData.splice(i, 1, replaceBarData);
        break;
      }
    }

    this.barData = newBarData;
    this.postEventMessage({
      type: EEventMessageType.CHART_PATCH,
      data: newBarData
    });
  };

  // Set as public for usage in useSymbolTopic
  public massageBarData = (newDataItem: ICustomBarData) => {
    const { selectedPeriod, isChartReady, activeSymbol, selectedProvider } = this.context;
    const { barData, useNewDataAsOpen } = this;

    if (selectedProvider === EChartProvider.TRADING_VIEW) {
      // Handle transformation of bar data at TV side instead due to lack of full bar data
      this.postEventMessage({
        type: EEventMessageType.SYMBOL_FEED,
        data: newDataItem
      });
      return;
    }
    const onUpdateCurrentBar = (newChartData: IChartBarData[]) => {
      this.useNewDataAsOpen = false;
      this.barData = newChartData as ICustomBarData[];

      const lastData = newChartData[newChartData.length - 1];
      this.postEventMessage({
        type: EEventMessageType.SYMBOL_FEED,
        data: lastData as ICustomBarData
      });
    };
    const onGenerateNewBar = (newChartData: IChartBarData[]) => {
      this.barData = newChartData as ICustomBarData[];
      this.postEventMessage({
        type: EEventMessageType.CHART_FEED,
        data: newChartData as ICustomBarData[]
      });
      this.useNewDataAsOpen = true;
    };
    handleIncomingBarData({
      newDataItem: newDataItem as IChartBarData,
      selectedPeriod,
      loading: !isChartReady,
      activeSymbol: activeSymbol || '',
      barData: barData as IChartBarData[],
      useNewDataAsOpen,
      onUpdateCurrentBar,
      onGenerateNewBar
    });
  };

  private mapChartPeriodToTopicPeriod = (period: EChartPeriod) => {
    const chartToTopicMap: Record<EChartPeriod, EChartTopicPeriod> = {
      [EChartPeriod.MINUTE_1]: EChartTopicPeriod.MINUTE_1,
      [EChartPeriod.MINUTE_2]: EChartTopicPeriod.MINUTE_2,
      [EChartPeriod.MINUTE_3]: EChartTopicPeriod.MINUTE_3,
      [EChartPeriod.MINUTE_4]: EChartTopicPeriod.MINUTE_4,
      [EChartPeriod.MINUTE_5]: EChartTopicPeriod.MINUTE_5,
      [EChartPeriod.MINUTE_6]: EChartTopicPeriod.MINUTE_6,
      [EChartPeriod.MINUTE_10]: EChartTopicPeriod.MINUTE_10,
      [EChartPeriod.MINUTE_15]: EChartTopicPeriod.MINUTE_15,
      [EChartPeriod.MINUTE_20]: EChartTopicPeriod.MINUTE_20,
      [EChartPeriod.MINUTE_30]: EChartTopicPeriod.MINUTE_30,
      [EChartPeriod.HOUR_1]: EChartTopicPeriod.HOUR_1,
      [EChartPeriod.HOUR_2]: EChartTopicPeriod.HOUR_2,
      [EChartPeriod.HOUR_3]: EChartTopicPeriod.HOUR_3,
      [EChartPeriod.HOUR_4]: EChartTopicPeriod.HOUR_4,
      [EChartPeriod.HOUR_6]: EChartTopicPeriod.HOUR_6,
      [EChartPeriod.DAY]: EChartTopicPeriod.DAY,
      [EChartPeriod.WEEK]: EChartTopicPeriod.WEEK,
      [EChartPeriod.MONTH]: EChartTopicPeriod.MONTH
    };

    return chartToTopicMap[period] ?? EChartTopicPeriod.MINUTE_1;
  };

  // Indicator Configuration
  public setColorPickerVisible = (isVisible: boolean) => {
    this.context.colorPickerVisible = isVisible;
  };

  public setSelectedIndicatorTab = (tab: EIndicatorConfigType) => {
    this.context.selectedIndicatorTab = tab;
  };

  public getCandlestickColorUserPreference = async () => {
    await userPreferenceService.getCandlestickColorUserPreference(true).then((result) => {
      const { positive, negative } = result;
      this.postEventMessage({
        type: EEventMessageType.UPDATE_CANDLESTICK_COLOR,
        data: {
          positive,
          negative
        }
      });
    });
  };

  private emitEnabledIndicatorsToChart = () => {
    technicalIndicatorService.context.technicalIndicators.forEach((indicator) => {
      if (indicator.enabled) {
        this.postEventMessage({
          type: EEventMessageType.INDICATOR,
          data: indicator.type
        });
      }
    });
  };

  public resetToDefaultContext = () => {
    this.context = { ...defaultContext };
  };
}

export default new ChartService();
